import { format } from "date-fns";
import { useEffect, useState } from "react";

import type { RevMetaType, RevType } from "../../../shared/types/sharedb";
import type { WorksheetType } from "../../../shared/types/worksheet";

import { serverGetPageEvents } from "api/api-server-data";
import { getRevs } from "api/api-worksheets";
import { DEBUG_TIMELINE_QUERY_PARAM, TIMELINE_VIEW_QUERY_PARAM } from "components/constants";
import { PageEventType } from "components/server-types";
import { t } from "i18n/i18n";
import { useAppDispatch, useAppSelector } from "store/index";
import { selectCurrentSectionId, selectCurrentUserId, userSelectors } from "store/selectors";
import { viewStateActions } from "store/slices/view";
import { toastLocalizedOperationError } from "utils/alerts";
import { useQueryParam, useQueryParamsActions } from "utils/routing";
import { getUserFullName } from "utils/user-utils";
import { getFakeDoc } from "worksheets/helpers/fake-doc";
import {
  type WorksheetSnapshot,
  buildIdleSnapshots,
  buildSnapshots,
  snapshotHasScrubberMark,
} from "worksheets/helpers/timeline-snapshots";
import { Worksheet } from "worksheets/views/Worksheet";
import { VersionHistoryCard } from "worksheets/views/timeline/VersionHistoryCard";

const SHORT_TIMEZONE_FORMAT = new Intl.DateTimeFormat(undefined, { timeZoneName: "short" });

type TimelineWorksheetDebug = {
  snapshotIndex: number;
  /** ShareDB edit ops after document create (does not include the create op). */
  editRevisions: RevType[];
  /** Doc state at each slider position; index 0 is the create snapshot. */
  snapshots: WorksheetSnapshot[];
  currentSnapshot: { doc: WorksheetType; meta: RevMetaType };
  /** Op that produced the current snapshot; null at index 0 (create). */
  editThatProducedCurrentView: RevType | null;
  /** Op from the current snapshot to the next; null on the last snapshot. */
  editToNextSnapshot: RevType | null;
};

declare global {
  interface Window {
    timelineWorksheet?: TimelineWorksheetDebug;
  }
}

const formatTimelineDate = (date: Date): string => {
  const timezone = SHORT_TIMEZONE_FORMAT.formatToParts(date)?.find(
    (part) => part.type === "timeZoneName",
  )?.value;

  return `${format(date, "MMM dd, yyyy, h:mm a")}${timezone ? ` ${timezone}` : ""}`;
};

const eventLabelForSnapshot = (
  snapshot: WorksheetSnapshot,
  currentUserId: string | null,
  editorNames: string[],
  isFirstVersion: boolean,
): { label: string; tooltip?: string } => {
  const { eventType, userIds } = snapshot;

  if (eventType === PageEventType.PUBLICATION) {
    return { label: t("worksheet.timeline.event_publication") };
  }
  if (eventType === PageEventType.SUBMISSION_COLLECTION) {
    return { label: t("worksheet.timeline.event_collection") };
  }
  if (isFirstVersion) {
    return { label: "" };
  }
  if (userIds.length === 1 && userIds[0] === currentUserId) {
    return { label: t("worksheet.timeline.event_edited_current_user") };
  }

  const names = editorNames.join(", ");
  return {
    label: t("worksheet.timeline.event_edited", { userName: names }),
    tooltip: editorNames.length > 2 ? names : undefined,
  };
};

export const TimelineWorksheet = ({ docId, pageId }: { docId: string; pageId: string }) => {
  const dispatch = useAppDispatch();
  const { deleteQueryParams } = useQueryParamsActions();
  const [snapshotIndex, setSnapshotIndex] = useState(0);
  const [snapshots, setSnapshots] = useState<WorksheetSnapshot[]>([]);
  const [editRevisions, setEditRevisions] = useState<RevType[] | null>(null);
  const isDebugTimeline = useQueryParam(DEBUG_TIMELINE_QUERY_PARAM) === "1";
  const currentUserId = useAppSelector(selectCurrentUserId);
  const currentSectionId = useAppSelector(selectCurrentSectionId);

  // Full entities map needed for revision author name lookups; can't narrow with useObjectSelector.
  // eslint-disable-next-line @minervaproject/no-object-from-use-app-selector/no-object-from-use-app-selector
  const users = useAppSelector(userSelectors.selectEntities);

  useEffect(() => {
    if (!currentSectionId) {
      return;
    }

    let cancelled = false;

    const loadTimeline = async () => {
      const response = await getRevs(docId, currentSectionId);
      const [createRevision, ...editsAfterCreate] = response.data;
      if (!createRevision) {
        return;
      }

      let builtSnapshots: WorksheetSnapshot[];
      if (isDebugTimeline) {
        builtSnapshots = buildSnapshots(createRevision, editsAfterCreate);
      } else {
        const pageEvents = await serverGetPageEvents(pageId);
        builtSnapshots = buildIdleSnapshots(createRevision, editsAfterCreate, pageEvents);
      }

      if (cancelled) {
        return;
      }

      if (builtSnapshots.length === 0) {
        setEditRevisions(editsAfterCreate);
        setSnapshots([]);
        return;
      }

      setEditRevisions(editsAfterCreate);
      setSnapshots(builtSnapshots);
      setSnapshotIndex(0);
    };

    loadTimeline().catch(() => toastLocalizedOperationError("failed_to_retrieve_revisions"));

    return () => {
      cancelled = true;
    };
  }, [docId, pageId, currentSectionId, isDebugTimeline]);

  if (editRevisions === null) {
    return (
      <div className="mt-4 flex items-center justify-center">{t("common.loading_ellipsis")}</div>
    );
  }

  if (snapshots.length === 0) {
    return (
      <div className="mt-4 flex items-center justify-center">
        {t("worksheet.timeline.no_revisions")}
      </div>
    );
  }

  const snapshot = snapshots[snapshotIndex];
  const { doc, meta, userIds } = snapshot;
  const dateLabel = formatTimelineDate(new Date(meta.ts));
  const editorNames = userIds.map((userId) => getUserFullName(users[userId] || null));
  const { label: eventLabel, tooltip: eventLabelTooltip } = eventLabelForSnapshot(
    snapshot,
    currentUserId,
    editorNames,
    snapshotIndex === 0,
  );
  const marks = snapshots.flatMap((item, index) => {
    if (!snapshotHasScrubberMark(item.marks)) {
      return [];
    }

    const markEditorNames = item.userIds.map((userId) => getUserFullName(users[userId] || null));
    const { label: markEventLabel } = eventLabelForSnapshot(
      item,
      currentUserId,
      markEditorNames,
      index === 0,
    );
    const eventType =
      item.eventType === PageEventType.PUBLICATION ||
      item.eventType === PageEventType.SUBMISSION_COLLECTION
        ? item.eventType
        : undefined;

    return [
      {
        index,
        dateLabel: formatTimelineDate(new Date(item.meta.ts)),
        eventLabel: markEventLabel,
        eventType,
      },
    ];
  });

  const skeletonDocWithHistoricData = getFakeDoc(doc);

  window.timelineWorksheet = {
    snapshotIndex,
    editRevisions,
    snapshots,
    currentSnapshot: { doc, meta },
    editThatProducedCurrentView:
      isDebugTimeline && snapshotIndex > 0 ? (editRevisions[snapshotIndex - 1] ?? null) : null,
    editToNextSnapshot:
      isDebugTimeline && snapshotIndex < editRevisions.length
        ? (editRevisions[snapshotIndex] ?? null)
        : null,
  };

  return (
    <div className="flex w-full flex-col items-center">
      <VersionHistoryCard
        className="pb-4"
        dateLabel={dateLabel}
        eventLabel={eventLabel}
        eventLabelTooltip={eventLabelTooltip}
        marks={marks}
        snapshotCount={snapshots.length}
        snapshotIndex={snapshotIndex}
        onChange={setSnapshotIndex}
        onClose={() => {
          dispatch(viewStateActions.setIsTimelineView(false));
          deleteQueryParams([TIMELINE_VIEW_QUERY_PARAM, DEBUG_TIMELINE_QUERY_PARAM]);
        }}
      />

      <Worksheet doc={skeletonDocWithHistoricData} />
    </div>
  );
};
